import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import { Refresh, Search, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useThemeMode } from '../hooks/useThemeMode';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const PAGE_SIZE = 50;

function Leaderboard() {
  const { isDark } = useThemeMode();

  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cachedAt, setCachedAt] = useState(null);
  const [totalDepositors, setTotalDepositors] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);

  const fetchLeaderboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/v2/leaderboard?limit=10000`);
      const data = await response.json();
      if (data.success) {
        setLeaderboard(data.leaderboard || []);
        setCachedAt(data.cached_at);
        setTotalDepositors(data.total_unique_depositors || 0);
      } else {
        setError(data.error || 'Failed to load leaderboard');
      }
    } catch (err) {
      setError('Network error loading leaderboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return leaderboard;
    const q = searchQuery.trim().toLowerCase();
    return leaderboard.filter((entry) => entry.depositor.includes(q));
  }, [leaderboard, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageEntries = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Reset to first page when search changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: isDark ? '#e5e5e5' : '#171717' }}>
            Leaderboard
          </Typography>
          {totalDepositors > 0 && (
            <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mt: 0.5 }}>
              {totalDepositors} unique depositor{totalDepositors !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchLeaderboard} disabled={loading} size="small">
            {loading ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search by wallet address..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
            '& fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
            '&:hover fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)' },
          },
          '& .MuiInputBase-input': {
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ fontSize: 18, color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' }} />
            </InputAdornment>
          ),
        }}
      />

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && leaderboard.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && leaderboard.length === 0 && !error && (
        <Paper
          sx={{
            textAlign: 'center',
            py: 6,
            border: `1px solid ${isDark ? '#262626' : '#e5e5e5'}`,
            borderRadius: 3,
          }}
        >
          <Typography variant="body1" sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
            No deposits recorded yet. Be the first depositor!
          </Typography>
        </Paper>
      )}

      {leaderboard.length > 0 && filtered.length === 0 && searchQuery.trim() && (
        <Paper
          sx={{
            textAlign: 'center',
            py: 4,
            border: `1px solid ${isDark ? '#262626' : '#e5e5e5'}`,
            borderRadius: 3,
          }}
        >
          <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
            No results for "{searchQuery}"
          </Typography>
        </Paper>
      )}

      {pageEntries.length > 0 && (
        <Stack spacing={1}>
          {pageEntries.map((entry) => (
            <Paper
              key={entry.depositor}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                py: 1.5,
                px: 2,
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)'}`,
                borderRadius: 2,
                transition: 'border-color 0.15s',
                '&:hover': {
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
                },
              }}
            >
              {/* Rank */}
              <Chip
                label={`#${entry.rank}`}
                size="small"
                sx={{
                  fontWeight: 700,
                  minWidth: 40,
                  height: 26,
                  fontSize: '0.8rem',
                  bgcolor: entry.rank <= 3
                    ? ['', 'rgba(255, 215, 0, 0.15)', 'rgba(192, 192, 192, 0.15)', 'rgba(205, 127, 50, 0.15)'][entry.rank]
                    : isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                  color: entry.rank <= 3
                    ? ['', '#FFD700', '#C0C0C0', '#CD7F32'][entry.rank]
                    : isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                  border: entry.rank <= 3
                    ? `1px solid ${['', 'rgba(255,215,0,0.4)', 'rgba(192,192,192,0.4)', 'rgba(205,127,50,0.4)'][entry.rank]}`
                    : 'none',
                }}
              />

              {/* Address */}
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  color: isDark ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
                  fontSize: '0.85rem',
                  minWidth: 120,
                }}
              >
                {entry.depositor_short}
              </Typography>

              {/* Deposit count */}
              <Chip
                label={`${entry.deposit_count} deposit${entry.deposit_count !== 1 ? 's' : ''}`}
                size="small"
                sx={{
                  height: 24,
                  fontSize: '0.75rem',
                  bgcolor: 'rgba(167, 139, 250, 0.12)',
                  color: '#a78bfa',
                  fontWeight: 600,
                }}
              />

              {/* Token breakdown */}
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
                {entry.token_breakdown.map((tb, i) => (
                  <Chip
                    key={i}
                    label={`${tb.total} ${tb.symbol}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.75rem',
                      height: 24,
                      borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
                      color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)',
                    }}
                  />
                ))}
              </Box>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Pagination */}
      {filtered.length > PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 2 }}>
          <Button
            size="small"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            sx={{ minWidth: 36, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}
          >
            <ChevronLeft fontSize="small" />
          </Button>
          <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '0.8rem' }}>
            Page {page + 1} of {totalPages}
          </Typography>
          <Button
            size="small"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            sx={{ minWidth: 36, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}
          >
            <ChevronRight fontSize="small" />
          </Button>
        </Box>
      )}

      {cachedAt && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 2,
            textAlign: 'right',
            color: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)',
            fontSize: '0.7rem',
          }}
        >
          Updated: {new Date(cachedAt).toLocaleString()}
        </Typography>
      )}
    </Box>
  );
}

export default Leaderboard;
